import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, TrendingUp, TrendingDown, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import { useSmartMoneyStore } from '../../../state/smartMoneyStore';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import ExportButton from '../../../components/ui/ExportButton';
import SectionNav from '../../../components/layout/SectionNav';
import { discoverTabs } from '../../../nav/publicNav';

const HOLDER_TABS = [
  { key: 'fii', label: 'FII' },
  { key: 'dii', label: 'DII' },
  { key: 'promoter', label: 'Promoter' },
  { key: 'public', label: 'Public' },
];

function ChangeBadge({ change }) {
  if (change == null) return <span className="text-muted-foreground">—</span>;
  const positive = change > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${positive ? 'text-emerald-600' : 'text-red-500'}`}>
      {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {positive ? '+' : ''}{change.toFixed(2)}%
    </span>
  );
}

export default function SmartMoneyPage() {
  const navigate = useNavigate();
  const {
    items, total, page, pageSize, loading, error,
    holderType, direction, minChange,
    setHolderType, setDirection, setMinChange, fetch: fetchData,
  } = useSmartMoneyStore();

  useEffect(() => { fetchData(); }, []);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <SectionNav tabs={discoverTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <ArrowUpDown className="w-5 h-5 text-emerald-500" /> Smart Money Moves
        </h1>
        <p className="text-xs text-muted-foreground">Quarter-over-quarter shareholding changes across the market</p>
      </div>

      {/* Holder type tabs */}
      <div className="flex gap-0 border-b border-border">
        {HOLDER_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setHolderType(tab.key)}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
              holderType === tab.key
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex gap-1">
          {[
            { key: '', label: 'All' },
            { key: 'increase', label: 'Increases' },
            { key: 'decrease', label: 'Decreases' },
          ].map(d => (
            <Button
              key={d.key}
              variant={direction === d.key ? 'default' : 'outline'}
              size="sm"
              className={`text-xs h-7 ${direction === d.key ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              onClick={() => setDirection(d.key)}
            >
              {d.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Min change:
          <input
            type="number"
            step="0.1"
            value={minChange}
            onChange={(e) => setMinChange(parseFloat(e.target.value) || 0)}
            onBlur={() => fetchData(1)}
            className="w-16 h-7 px-2 text-xs rounded-md border border-border bg-background focus:outline-none"
          />
          %
        </div>
        <span className="text-xs text-muted-foreground ml-auto mr-2">{total.toLocaleString()} results</span>
        <ExportButton rows={items} filename={`smart-money-${holderType}`} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-emerald-800 text-white">
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Company</th>
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Symbol</th>
                  <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Previous %</th>
                  <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Latest %</th>
                  <th className="text-right py-2 px-3 font-semibold text-[10px] uppercase">Change</th>
                  <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase">Quarter</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={i} className="border-b border-muted/20 hover:bg-muted/20">
                    <td className="py-2 px-3 font-medium">
                      <button
                        className="hover:text-emerald-600 hover:underline text-left"
                        onClick={() => r.symbol && navigate(`/public/india-company/${r.symbol}`)}
                      >
                        {r.company_name}
                      </button>
                    </td>
                    <td className="py-2 px-3">{r.symbol || '—'}</td>
                    <td className="py-2 px-3 text-right text-muted-foreground">
                      {r.pct_prev != null ? r.pct_prev.toFixed(2) + '%' : '—'}
                    </td>
                    <td className="py-2 px-3 text-right font-medium">
                      {r.pct_latest != null ? r.pct_latest.toFixed(2) + '%' : '—'}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <ChangeBadge change={r.change_pct} />
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">{r.quarter_latest}</td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-muted-foreground">
                      No shareholding changes found matching your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1}
                onClick={() => fetchData(page - 1)}>
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages}
                onClick={() => fetchData(page + 1)}>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
